import {
  Box,
  Fab,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { EditAction } from '../../../components/FloatingActionsButtons/components/EditAction';
import { DeleteAction } from '../../../components/FloatingActionsButtons/components/DeleteAction';
import { BORDER_COLOR } from '../../../constants';
import { ROUTES } from '../../../routes/Routes';
import {
  ValueDateRow,
  ValueRow,
  ValueTextRow,
} from '../../../components/table';
import { useGetItem } from '../../../reactQuery/opportunities';
import { StatusBadge } from '../../../components/tabs/tab-panels/OpportunitiesTabPanel/components/StatusBadge';
import { DeleteItemDialog } from '../dialogs/DeleteItemDialog';
import { UpsertItemDialog } from '../dialogs/UpsertItemDialog';
import { CloseItemDialog } from '../dialogs/CloseItemDialog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const OpportunityDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);

  const navigate = useNavigate();
  const params = useParams();

  const opportunityId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: ROUTES.opportunities.overview,
    },
  ];

  const TableCellHeading = styled(TableCell)(() => ({
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  }));

  const { data: item, isLoading, error } = useGetItem(opportunityId);

  const handleOpportunityNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'opportunity',
      }),
      {
        variant: 'error',
      }
    );
    navigate(ROUTES.opportunities.overview);
  };

  useEffect(() => {
    if (Number.isNaN(opportunityId)) {
      handleOpportunityNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleOpportunityNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [opportunityId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!item) {
    navigate(ROUTES.opportunities.overview);
    return <Box>Error</Box>;
  }

  const onDeleteItem = (): void => {
    navigate(ROUTES.opportunities.overview);
  };

  return (
    <Fragment>
      <PageTitle title={t('opportunities:details.title')} space={'double'}>
        <Breadcrumbs crumbs={crumbs} current={item.title} />
      </PageTitle>
      <TableContainer
        component={Paper}
        sx={{
          borderLeft: `1px solid ${BORDER_COLOR}`,
        }}
      >
        <Table>
          <TableBody>
            <ValueRow
              label={t('entities:opportunityItem.title')}
              value={item.title}
            />
            <ValueRow
              label={t('entities:opportunityItem.stage')}
              value={item.stage?.name}
            />
            <ValueRow
              label={t('entities:opportunityItem.client')}
              value={item.client.name}
            />
            <ValueRow
              label={t('entities:opportunityItem.product')}
              value={item.product.name}
            />
            <ValueRow
              label={t('entities:opportunityItem.contact')}
              value={`${item.contact.firstName} ${item.contact.lastName}`}
            />
            <ValueRow
              label={t('entities:opportunityItem.agent')}
              value={`${item.agent.firstName} ${item.agent.lastName}`}
            />
            <ValueRow label={t('entities:opportunityItem.status')}>
              <StatusBadge
                label={t(
                  `entities:opportunityItem.enums.status.${item.status}`
                )}
                status={item.status}
              />
            </ValueRow>
            {item.status !== 'open' && item.closedAt && (
              <Fragment>
                <ValueRow
                  label={t('entities:opportunityItem.closedBy')}
                  value={
                    item.closedBy
                      ? `${item.closedBy.firstName} ${item.closedBy.lastName}`
                      : undefined
                  }
                />
                <ValueDateRow
                  label={t('entities:opportunityItem.closedAt')}
                  date={new Date(item.closedAt)}
                />
              </Fragment>
            )}
            {item.status === 'failed' && (
              <ValueTextRow
                label={t('entities:opportunityItem.reason')}
                value={item.reason}
              />
            )}
            <ValueDateRow
              label={t('entities:common.createdAt')}
              date={new Date(item.createdAt)}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <FloatingActionsButtons>
        <DeleteAction
          onClick={() => {
            setShowDeleteDialog(true);
          }}
        />
        <EditAction
          onClick={() => {
            setShowEditDialog(true);
          }}
        />
        {item?.status === 'open' && (
          <Fab color="success" onClick={() => setShowCloseDialog(true)}>
            <CheckCircleOutlineIcon />
          </Fab>
        )}
      </FloatingActionsButtons>
      <DeleteItemDialog
        show={showDeleteDialog}
        item={item}
        onClose={() => setShowDeleteDialog(false)}
        onDeleted={onDeleteItem}
      />
      <UpsertItemDialog
        item={item}
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />
      <CloseItemDialog
        item={item}
        show={showCloseDialog}
        onClose={() => setShowCloseDialog(false)}
      />
    </Fragment>
  );
};
